<div class="modal-header">
  <div class="modal-title-wrapper">
    <h2 class="modal-title fw-bold">{{ 'manageEvent.orderDetails.orderNum' | translate }} #{{ order.order_id }}</h2>
    <div class="order-meta d-flex gap-4 mt-2">
      <span class="badge {{ getStatusBadgeClass(order.status) }} fs-7">{{ getStatusLabel(order.status) }}</span>
      <span class="text-muted">
        <i class="fa fa-calendar me-2"></i>
        {{ formatDateTime(order.created_at) }}
      </span>
    </div>
  </div>
  <button type="button" class="btn-close" (click)="close()"></button>
</div>

<div class="modal-body">
  <div class="row g-5">
    <!-- Customer Information -->
    <div class="col-md-6">
      <div class="card bg-light">
        <div class="card-body">
          <h5 class="fw-bold mb-4">
            <i class="fa fa-user me-2 text-primary"></i>
            {{ 'manageEvent.orderDetails.customerInfo' | translate }}
          </h5>
          @if (order.user) {
            <div class="d-flex flex-column gap-2">
              <div class="d-flex justify-content-between">
                <span class="text-muted">{{ 'manageEvent.orderDetails.name' | translate }}:</span>
                <span class="fw-semibold">{{ getUserFullName() }}</span>
              </div>
              @if (order.user.email) {
                <div class="d-flex justify-content-between">
                  <span class="text-muted">{{ 'manageEvent.orderDetails.email' | translate }}:</span>
                  <span class="fw-semibold">{{ order.user.email }}</span>
                </div>
              }
              @if (order.user.phone) {
                <div class="d-flex justify-content-between">
                  <span class="text-muted">{{ 'manageEvent.orderDetails.phone' | translate }}:</span>
                  <span class="fw-semibold">{{ order.user.phone }}</span>
                </div>
              }
              <div class="d-flex justify-content-between">
                <span class="text-muted">{{ 'manageEvent.orderDetails.userId' | translate }}:</span>
                <span class="fw-semibold text-gray-600 fs-7">{{ order.user_id }}</span>
              </div>
            </div>
          } @else {
            <p class="text-muted mb-0">{{ 'manageEvent.orderDetails.noCustomerInfo' | translate }}</p>
          }
        </div>
      </div>
    </div>

    <!-- Transaction Information -->
    <div class="col-md-6">
      <div class="card bg-light">
        <div class="card-body">
          <h5 class="fw-bold mb-4">
            <i class="fa fa-credit-card me-2 text-primary"></i>
            {{ 'manageEvent.orderDetails.transactionInfo' | translate }}
          </h5>
          <div class="d-flex flex-column gap-2">
            <div class="d-flex justify-content-between">
              <span class="text-muted">{{ 'manageEvent.orderDetails.status' | translate }}:</span>
              <span class="badge {{ getStatusBadgeClass(order.status) }}">{{ getStatusLabel(order.status) }}</span>
            </div>
            <div class="d-flex justify-content-between">
              <span class="text-muted">{{ 'manageEvent.orderDetails.created' | translate }}:</span>
              <span class="fw-semibold">{{ formatDateTime(order.created_at) }}</span>
            </div>
            @if (order.paid_at) {
              <div class="d-flex justify-content-between">
                <span class="text-muted">{{ 'manageEvent.orderDetails.paidAt' | translate }}:</span>
                <span class="fw-semibold text-success">{{ formatDateTime(order.paid_at) }}</span>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Order Lines -->
  <div class="card mt-5">
    <div class="card-header">
      <h5 class="card-title fw-bold mb-0">
        <i class="fa fa-list me-2 text-primary"></i>
        {{ 'manageEvent.orderDetails.orderItems' | translate }} ({{ order.order_lines?.length || 0 }})
      </h5>
    </div>
    <div class="card-body p-0">
      @if (order.order_lines && order.order_lines.length > 0) {
        <div class="table-responsive">
          <table class="table table-row-bordered mb-0">
            <thead class="bg-light">
              <tr class="fw-bold text-muted">
                <th class="ps-5">{{ 'manageEvent.orderDetails.tableItem' | translate }}</th>
                <th>{{ 'manageEvent.orderDetails.tableType' | translate }}</th>
                <th class="text-center">{{ 'manageEvent.orderDetails.tableQty' | translate }}</th>
                <th class="text-end">{{ 'manageEvent.orderDetails.tableUnitPrice' | translate }}</th>
                <th class="text-end">{{ 'manageEvent.orderDetails.tableVatPercent' | translate }}</th>
                <th class="text-end">{{ 'manageEvent.orderDetails.tableVat' | translate }}</th>
                <th class="text-end pe-5">{{ 'manageEvent.orderDetails.tableTotal' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (line of order.order_lines; track line.order_line_id) {
                <tr>
                  <td class="ps-5">
                    <div class="d-flex flex-column">
                      <span class="fw-semibold text-gray-800">{{ line.description }}</span>
                      @if (line.class_registration_id) {
                        <span class="text-muted fs-7">{{ 'manageEvent.orderDetails.registrationId' | translate }}: {{ line.class_registration_id }}</span>
                      }
                      @if (line.service_registration_id) {
                        <span class="text-muted fs-7">{{ 'manageEvent.orderDetails.registrationId' | translate }}: {{ line.service_registration_id }}</span>
                      }
                    </div>
                  </td>
                  <td>
                    <span class="badge badge-light-primary">
                      <i class="fa {{ getLineTypeIcon(line.line_type) }} me-1"></i>
                      {{ getLineTypeLabel(line.line_type) }}
                    </span>
                  </td>
                  <td class="text-center">{{ line.quantity }}</td>
                  <td class="text-end">{{ formatCents(line.unit_price_cents) }} {{ order.currency }}</td>
                  <td class="text-end">{{ line.vat_rate || 0 }}%</td>
                  <td class="text-end">{{ formatCents(line.vat_cents || 0) }} {{ order.currency }}</td>
                  <td class="text-end pe-5 fw-bold">{{ formatCents(line.total_cents) }} {{ order.currency }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <div class="text-center py-8 text-muted">
          <i class="fa fa-box-open fs-2x mb-3 d-block opacity-50"></i>
          <p class="mb-0">{{ 'manageEvent.orderDetails.noOrderItems' | translate }}</p>
        </div>
      }
    </div>
  </div>

  <!-- Order Summary -->
  <div class="card mt-5 bg-light-primary">
    <div class="card-body">
      <h5 class="fw-bold mb-4">
        <i class="fa fa-calculator me-2 text-primary"></i>
        {{ 'manageEvent.orderDetails.orderSummary' | translate }}
      </h5>
      <div class="d-flex flex-column gap-3">
        <div class="d-flex justify-content-between">
          <span class="text-muted">{{ 'manageEvent.orderDetails.sumExVat' | translate }}:</span>
          <span class="fw-semibold">{{ formatCents(getSubtotalExVat()) }} {{ order.currency }}</span>
        </div>
        @for (vatGroup of getVatBreakdown(); track vatGroup.rate) {
          <div class="d-flex justify-content-between">
            <span class="text-muted">{{ 'manageEvent.orderDetails.vat' | translate }} {{ vatGroup.rate }}%:</span>
            <span class="fw-semibold">{{ formatCents(vatGroup.amount) }} {{ order.currency }}</span>
          </div>
        }
        <div class="d-flex justify-content-between">
          <span class="text-muted">{{ 'manageEvent.orderDetails.totalVat' | translate }}:</span>
          <span class="fw-semibold">{{ formatCents(order.total_vat_cents || 0) }} {{ order.currency }}</span>
        </div>
        <div class="separator my-2"></div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">{{ 'manageEvent.orderDetails.subtotalInclVat' | translate }}:</span>
          <span class="fw-semibold">{{ formatCents(order.subtotal_cents) }} {{ order.currency }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">{{ 'manageEvent.orderDetails.platformFee' | translate }} ({{ getPlatformFeePercentage() }}%):</span>
          <span class="fw-semibold">{{ formatCents(order.platform_fee_cents) }} {{ order.currency }}</span>
        </div>
        <div class="separator my-2"></div>
        <div class="d-flex justify-content-between">
          <span class="fw-bold fs-4">{{ 'manageEvent.orderDetails.total' | translate }}:</span>
          <span class="fw-bold fs-4 text-primary">{{ formatCents(order.total_cents) }} {{ order.currency }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Event Information (optional) -->
  @if (event) {
    <div class="card mt-5">
      <div class="card-body">
        <h5 class="fw-bold mb-4">
          <i class="fa fa-calendar-check me-2 text-primary"></i>
          {{ 'manageEvent.orderDetails.eventInfo' | translate }}
        </h5>
        <div class="d-flex align-items-center">
          @if (event.image_url) {
            <img [src]="event.image_url" alt="Event" class="rounded me-4" style="width: 60px; height: 60px; object-fit: cover;">
          }
          <div>
            <div class="fw-bold text-gray-800">{{ event.event_name }}</div>
            @if (event.start_date) {
              <div class="text-muted fs-7">
                <i class="fa fa-calendar me-1"></i>
                {{ formatDateTime(event.start_date) }}
              </div>
            }
            @if (event.city || event.country) {
              <div class="text-muted fs-7">
                <i class="fa fa-map-marker-alt me-1"></i>
                {{ event.city }}@if(event.city && event.country){, }{{ event.country }}
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  }
</div>

<div class="modal-footer">
  <app-button
    [variant]="'outline'"
    [label]="'common.close' | translate"
    (clicked)="close()"
  />
  <app-button
    [variant]="'primary'"
    [label]="'manageEvent.orderDetails.downloadReceipt' | translate"
    [iconClass]="'fa fa-file-pdf'"
    [loading]="generatingPdf"
    (clicked)="downloadReceipt()"
  />
</div>
